import styled from '@emotion/styled'
import { AnimatedLabel } from 'app/components/Common/Animation/AnimatedLabel'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { Media } from 'app/theme/media'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'
import { LazyLoadComponent } from 'react-lazy-load-image-component'
import { Parallax } from 'react-scroll-parallax'

export interface Props {
  cta?: ButtonProps
  description?: string
  image?: ImageProps
  label?: string
  thumb?: ImageProps
  title?: string
}

export const Service = memo(function Service({
  cta,
  description,
  image,
  label,
  thumb,
  title,
}: Props) {
  const [animObjectRef, isAnimObjectVisible] = useInView({
    triggerOnce: true,
  })

  return (
    <Container row stretch wrap>
      <LeftSide>
        <Background />

        <Wrapper
          className={isAnimObjectVisible ? 'visible' : undefined}
          ref={animObjectRef}
        >
          {label ? <Label text={label} /> : null}

          {title ? <Title length={20} text={title} /> : null}

          {description ? (
            <FadeInUp delay={0.2}>
              <Description dangerouslySetInnerHTML={{ __html: description }} />
            </FadeInUp>
          ) : null}

          {cta ? (
            <>
              <Media lessThan="desktopSmall">
                <CTA {...cta} variant="full-dark" />
              </Media>

              <Media greaterThanOrEqual="desktopSmall">
                <FadeInUp delay={0.3}>
                  <CTA {...cta} variant="full-dark" />
                </FadeInUp>
              </Media>
            </>
          ) : null}
        </Wrapper>

        {thumb ? (
          <Media greaterThanOrEqual="desktopSmall">
            <LazyLoadComponent>
              <FadeInUp>
                <Thumb>
                  <Image {...thumb} />
                </Thumb>
              </FadeInUp>
            </LazyLoadComponent>
          </Media>
        ) : null}
      </LeftSide>

      <RightSide>
        {image ? (
          <LazyLoadComponent>
            <Parallax translateY={['-20%', '20%']}>
              <Image {...image} />
            </Parallax>
          </LazyLoadComponent>
        ) : null}
      </RightSide>
    </Container>
  )
})

const LeftSide = styled.div`
  width: 50%;
  padding: 5rem 0 0 5vw;
  position: relative;

  @media (max-width: 1199px) {
    width: 100%;
    order: 2;
    padding: 0;
  }
`

const Background = styled.div`
  width: calc(100% - 13.889vw);
  height: 26.375rem;
  background: url('/svg/pattern-light.svg') top left;
  position: absolute;
  top: 0;
  left: 5vw;

  @media (max-width: 1199px) {
    display: none;
  }
`

const Wrapper = styled.div`
  width: calc(100% - 10.8125rem);
  background: ${({ theme }) => theme.colors.variants.neutralLight5};
  clip-path: inset(0% 0% 100% 0%);
  margin-left: auto;
  margin-right: -4.375rem;
  padding: 8.25rem 6rem;
  position: relative;
  z-index: 3;
  &.visible {
    clip-path: inset(0);
    transition: 1.4s ease-in-out;
  }

  @media (max-width: 1439px) {
    width: 100%;
  }

  @media (max-width: 1199px) {
    background: none;
    clip-path: inset(0);
    margin-right: 0;
    padding: 0;
    transition: none;
  }
`

const Label = styled(AnimatedLabel)``

const Title = styled(AnimatedTitle)`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.25rem;
  font-weight: 500;
  line-height: 2.0625rem;
  text-transform: uppercase;

  @media (max-width: 1199px) {
    font-size: 1.875rem;
    line-height: 1.875rem;
  }
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  margin-top: 1.875rem;

  @media (max-width: 1199px) {
    margin-top: 1.5rem;
  }
`

const CTA = styled(Button)`
  margin-top: 3rem;

  @media (max-width: 1199px) {
    margin-top: 1.875rem;
  }
`

const Thumb = styled.div`
  width: 16.875vw;
  aspect-ratio: 0.75;
  overflow: hidden;
  margin-top: -5rem;
  position: relative;
  z-index: 2;
`

const RightSide = styled.div`
  width: 50%;
  overflow: hidden;

  > div {
    height: 100%;
    position: relative;
  }

  @media (max-width: 1199px) {
    aspect-ratio: 1.33;
    width: 100%;
    margin-bottom: 1.875rem;

    > div {
      transform: none !important;
    }
  }
`

const Container = styled(FlexBox)`
  margin-top: 7.5rem;
  overflow: hidden;
  &:nth-of-type(even) {
    ${LeftSide} {
      order: 2;
      padding-right: 5vw;
      padding-left: 0;
    }
    ${Background} {
      background-position-x: right;
      right: 5vw;
      left: auto;
    }
    ${Wrapper} {
      margin-right: auto;
      margin-left: -4.375rem;
    }
    ${Thumb} {
      margin-left: auto;
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;
  }

  @media (max-width: 1199px) {
    margin-top: 5.625rem;
    padding: 0 1.875rem;
    &:nth-of-type(even) {
      ${LeftSide} {
        padding-right: 0;
      }
      ${Wrapper} {
        margin-left: 0;
      }
    }
  }
`
